import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Form, Button, Flex, Avatar, Typography, Col, message, Select } from 'antd';
import { FacebookOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { connectToAuth, getAgentContacts, getPlatforms } from '../../AgentServices';
import { useErrorHandler } from '../../../../Hooks/useErrorHandler';
import { isFacebookSDKLoaded, loginWithFacebookSdk } from '../../../../Utils/facebook';
import { facebookBusinessLoginConfigId } from '../../../../Config/facebookConfig';

const { Text } = Typography;

const PlatformSection = ({ form, mode, id, initialData }) => {
    const { handleError } = useErrorHandler();
    const [pages, setPages] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [tokenLoading, setTokenLoading] = useState(false);

    // Pagination and search states for both sections
    const [pagesSearch, setPagesSearch] = useState('');
    const [pagesPage, setPagesPage] = useState(1);
    const [hasMorePages, setHasMorePages] = useState(true);
    const [pagesLoading, setPagesLoading] = useState(false);

    const [contactsSearch, setContactsSearch] = useState('');
    const [contactsPage, setContactsPage] = useState(1);
    const [hasMoreContacts, setHasMoreContacts] = useState(true);
    const [contactsLoading, setContactsLoading] = useState(false);

    const searchTimeoutRef = useRef();

    // Initialize form fields
    useEffect(() => {
        if (initialData?.admin_contacts) {
            form.setFieldsValue({
                admins: initialData.admin_contacts.map((admin) => admin.id),
            });
        }
    }, [form, initialData]);

    const fetchPages = useCallback(async (page = 1, search = '') => {
        try {
            setPagesLoading(true);
            const { data, metadata } = await getPlatforms({
                page,
                size: 10,
                search
            });

            setPages(prev => page === 1 ? data : [...prev, ...data]);
            setHasMorePages(metadata.has_next);
            setPagesPage(page);

        } catch (error) {
            handleError(error);
        } finally {
            setPagesLoading(false);
        }
    }, [handleError]);

    const fetchContacts = useCallback(async (page = 1, search = '') => {
        if (!id) return;

        try {
            setContactsLoading(true);
            const { data, metadata } = await getAgentContacts({
                id,
                page,
                size: 10,
                search
            });
            setContacts(prev => page === 1 ? data : [...prev, ...data]);
            setHasMoreContacts(metadata.has_next);
            setContactsPage(page);

        } catch (error) {
            handleError(error);
        } finally {
            setContactsLoading(false);
        }
    }, [handleError, id]);

    useEffect(() => {
        fetchPages();
        if (mode === 'edit') fetchContacts();
    }, [fetchPages, fetchContacts, mode]);

    const handlePagesSearch = useCallback((search) => {
        clearTimeout(searchTimeoutRef.current);
        setPagesSearch(search);

        searchTimeoutRef.current = setTimeout(() => {
            fetchPages(1, search);
        }, 200);
    }, [fetchPages]);

    const handleContactsSearch = useCallback((search) => {
        clearTimeout(searchTimeoutRef.current);
        setContactsSearch(search);

        searchTimeoutRef.current = setTimeout(() => {
            fetchContacts(1, search);
        }, 200);
    }, [fetchContacts]);

    const handlePagesScroll = useCallback(async (event) => {
        const { target } = event;
        if (hasMorePages && !pagesLoading &&
            target.scrollTop + target.offsetHeight >= target.scrollHeight - 20) {
            await fetchPages(pagesPage + 1, pagesSearch);
        }
    }, [pagesPage, pagesSearch, hasMorePages, pagesLoading, fetchPages]);

    const handleContactsScroll = useCallback(async (event) => {
        const { target } = event;
        if (hasMoreContacts && !contactsLoading &&
            target.scrollTop + target.offsetHeight >= target.scrollHeight - 20) {
            await fetchContacts(contactsPage + 1, contactsSearch);
        }
    }, [contactsPage, contactsSearch, hasMoreContacts, contactsLoading, fetchContacts]);

    const handleFacebookLogin = useCallback(async () => {
        if (!isFacebookSDKLoaded) {
            message.error('Facebook SDK not loaded');
            return;
        }

        loginWithFacebookSdk(async (response) => {
            try {
                if (response.authResponse?.accessToken) {
                    await connectToAuth(response.authResponse.accessToken);
                    await fetchPages(1, '');
                } else {
                    message.error(response.error || 'Facebook login failed');
                }
            } catch (error) {
                handleError(error);
            }
        }, facebookBusinessLoginConfigId);
    }, [fetchPages, handleError]);

    const handleWhatsAppLogin = useCallback(async () => {
        setTokenLoading(true);
        try {
            const response = await fetch('YOUR_WHATSAPP_TOKEN_ENDPOINT', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ /* Add parameters */ }),
            });
            const data = await response.json();

            if (!response.ok) throw new Error(data.message || 'WhatsApp login failed');

            form.setFieldsValue({
                pageAccessToken: data.token,
                pageId: data.phoneNumberId,
                pageName: data.phoneNumber,
            });
        } catch (error) {
            handleError(error);
        } finally {
            setTokenLoading(false);
        }
    }, [form, handleError]);

    const handlePlatformChange = useCallback(() => {
        form.setFieldsValue({ page: '' });
    }, [form]);

    const isWhatsApp = useMemo(() =>
        form.getFieldValue('type') === 'whatsapp',
        [form]);

    const renderContactOption = useCallback((item, isAdmin = false) => (
        <Flex align="center" gap={12}>
            <Avatar
                src={item.picture_url}
                alt={isAdmin ? item.full_name : item.name}
                size="small"
            />
            <Flex vertical gap={0}>
                <Text>{isAdmin ? item.full_name : item.name}</Text>
                {isAdmin && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {item.email || item.phone}
                    </Text>
                )}
            </Flex>
        </Flex>
    ), []);

    const renderPageOptions = useMemo(() =>
        pages.map((page) => ({
            value: page.id,
            label: renderContactOption(page),
        })),
        [pages, renderContactOption]);

    const renderAdminsOptions = useMemo(() =>
        contacts.map((contact) => ({
            value: contact.id,
            label: renderContactOption(contact, true),
        })),
        [contacts, renderContactOption]);

    const labelRender = useCallback(({ value }) => {
        const page = pages.find((p) => p.id === value);
        return page ? renderContactOption(page) : null;
    }, [pages, renderContactOption]);

    return (
        <>
            <Col xs={24} md={12}>
                <Form.Item
                    name="type"
                    label="Platform"
                    rules={[{ required: true, message: 'Please select the platform type!' }]}
                    style={{ marginBottom: 4 }}
                >
                    <Select
                        placeholder="Select platform"
                        disabled={mode === 'edit'}
                        onChange={handlePlatformChange}
                        options={[
                            { value: 'messenger', label: 'Messenger' },
                            { value: 'instagram', label: 'Instagram (Coming Soon)', disabled: true },
                            { value: 'whatsapp', label: 'WhatsApp (Coming Soon)', disabled: true },
                        ]}
                    />
                </Form.Item>
            </Col>
            <Col xs={24} md={12}>
                {mode === "create" && <Button
                    type="primary"
                    style={{ flex: 1, width: '100%', marginBottom: 3 }}
                    icon={isWhatsApp ? <WhatsAppOutlined /> : <FacebookOutlined />}
                    onClick={isWhatsApp ? handleWhatsAppLogin : handleFacebookLogin}
                    loading={tokenLoading}
                >
                    {pages.length > 0 ? 'Refresh Token' : 'Get Token'}
                </Button>}
            </Col>
            {!isWhatsApp && (
                <Col xs={24} md={12}>
                    <Form.Item label="Page" required name="page" style={{ marginBottom: 4 }}>
                        <Select
                            placeholder="Search pages"
                            labelInValue
                            loading={pagesLoading}
                            disabled={mode === 'edit'}
                            style={{ width: '100%' }}
                            options={renderPageOptions}
                            showSearch
                            filterOption={false}
                            onSearch={handlePagesSearch}
                            onPopupScroll={handlePagesScroll}
                            value={form.getFieldValue('page') || undefined}
                        />
                    </Form.Item>
                </Col>
            )}
            {mode === 'edit' && (
                <Col xs={24} md={12}>
                    <Form.Item
                        label="Admins"
                        name="admins"
                        style={{ marginBottom: 4 }}
                        validateTrigger={['onChange']}
                        rules={[{ message: 'Please select at least one admin!' }]}
                    >
                        <Select
                            mode="multiple"
                            maxTagCount="responsive"
                            placeholder="Search admins"
                            loading={contactsLoading}
                            style={{ width: '100%' }}
                            options={renderAdminsOptions}
                            filterOption={false}
                            onSearch={handleContactsSearch}
                            onPopupScroll={handleContactsScroll}
                            showSearch
                            allowClear
                        />
                    </Form.Item>
                </Col>
            )}
        </>
    );
};

PlatformSection.propTypes = {
    form: PropTypes.object.isRequired,
    mode: PropTypes.oneOf(['edit', 'create']).isRequired,
    id: PropTypes.string,
    initialData: PropTypes.object,
};

export default PlatformSection;